import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';

/* =============================================================================
 * Native chrome
 * -----------------------------------------------------------------------------
 * The parts of the app that are the phone's, not the page's: the status bar at
 * the top, the splash while the webview boots, and what the keyboard does to
 * the layout when it opens.
 *
 * All of it is a no-op in a browser, so the same code runs both places.
 * ========================================================================== */

export const isNativeApp = () => Capacitor.isNativePlatform();

/**
 * Status bar icon colour, matched to the theme.
 *
 * Read the enum before changing this line. Capacitor names the styles after the
 * *background they are meant for*, not after the colour they produce:
 *
 *     Style.Dark  = light text  → for a DARK background
 *     Style.Light = dark text   → for a LIGHT background
 *
 * So a dark app theme wants `Style.Dark`. Reading it the other way round is
 * exactly how the clock ends up black on a black bar, and white on a white one
 * — invisible in both schemes, which is the symptom that led here.
 */
export async function applyStatusBar(scheme: 'light' | 'dark'): Promise<void> {
  if (!isNativeApp()) return;

  try {
    // Edge to edge, deliberately.
    //
    // This app targets SDK 36. From Android 15 (API 35) the platform *forces*
    // edge-to-edge and turns `Window.setStatusBarColor()` into a no-op — so
    // asking the plugin to paint the bar does nothing at all, and what shows
    // through is the window's own background.
    //
    // The only thing that works on a modern Android is to accept edge-to-edge
    // and paint that area ourselves: the webview extends under the status bar,
    // and the header's own background fills it. `--safe-top` reserves the
    // height so nothing lands underneath the clock.
    await StatusBar.setOverlaysWebView({ overlay: true });

    // Icon colour still works — it goes through WindowInsetsController rather
    // than the deprecated window property.
    await StatusBar.setStyle({ style: scheme === 'dark' ? Style.Dark : Style.Light });
  } catch (error) {
    console.warn('Status bar could not be styled', error);
  }
}

/**
 * Marks the document as running inside the app.
 *
 * CSS needs to know: a browser has no status bar to reserve room for, and
 * `env(safe-area-inset-top)` is legitimately 0 there.
 */
export function markNativeDocument(): void {
  if (!isNativeApp()) return;
  document.documentElement.classList.add('native');
  document.documentElement.dataset.platform = Capacitor.getPlatform();
}

/**
 * Android drops the status bar styling when the app returns from the
 * background, so it has to be reapplied rather than set once at launch.
 */
export function watchResume(reapply: () => void): () => void {
  if (!isNativeApp()) return () => {};

  /* addListener is asynchronous, so the returned stopper can be called before
     the bridge has handed back anything to stop. Without the flag that call
     does nothing, the handle arrives a moment later, and the listener is left
     registered with no reference left anywhere that could remove it — one more
     every time the shell remounts. See useAndroidBack for the same shape and
     what it cost there. */
  let dropped = false;
  let remove: (() => void) | undefined;

  void CapacitorApp.addListener('resume', reapply).then((handle) => {
    if (dropped) { void handle.remove(); return; }
    remove = () => void handle.remove();
  });

  return () => {
    dropped = true;
    remove?.();
  };
}

/**
 * Called once the app has actually rendered something, rather than on a timer.
 * A splash that disappears before the first paint shows a white flash; one that
 * lingers makes a fast launch feel slow.
 */
export async function hideSplash(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    /* already hidden */
  }
}

/**
 * When the keyboard opens, the webview shrinks. Publishing its height as a CSS
 * variable lets fixed bottom bars lift above it instead of being buried.
 */
export async function watchKeyboard(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    await Keyboard.addListener('keyboardWillShow', (info) => {
      document.documentElement.style.setProperty('--keyboard-h', `${info.keyboardHeight}px`);
      document.documentElement.classList.add('keyboard-open');
    });

    await Keyboard.addListener('keyboardWillHide', () => {
      document.documentElement.style.setProperty('--keyboard-h', '0px');
      document.documentElement.classList.remove('keyboard-open');
    });
  } catch (error) {
    console.warn('Keyboard listeners unavailable', error);
  }
}
